import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box/Box';
import IconButton from '@mui/material/IconButton/IconButton';
import useTheme from '@mui/material/styles/useTheme';
import Tooltip from '@mui/material/Tooltip/Tooltip';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useStore } from '../../../../store/store';
import { coursePageStyles } from './CoursePageStyle';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Title from '../../../../components/titleAndInput/Title';
import Button from '@mui/material/Button/Button';
import SettingsIcon from '@mui/icons-material/Settings';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import ButtonGroup from '@mui/material/ButtonGroup/ButtonGroup';
import Menu from '@mui/material/Menu/Menu';
import MenuItem from '@mui/material/MenuItem/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon/ListItemIcon';
import ListItemText from '@mui/material/ListItemText/ListItemText';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { getPageStatusColor } from '../../../../utils/courseDashboard/getPageStatusColor';
import PersonPinCircleIcon from '@mui/icons-material/PersonPinCircle';
import { CopyPageIcon, CopyPageSubpageIcon, DuplicatePageIcon, PersonPinCircleDashed } from '../../../../components/icons/Icons';
import useConfirm from '../../../../hooks/useConfirm';
import { Divider } from '@mui/material';
import { IPageItem } from './CoursePageList';
import { findNodeById } from '../../../../utils/courseDashboard/coureDashboardUtils';
import { v4 as uuidv4 } from 'uuid';

interface ICoursePageItemProps {
  nodeData: IPageItem; // Node data passed from the tree
  onExpand: (e: any, key: string) => void;
  handleNewPageItem: (item: IPageItem) => void;
  handleCopyPageItem: (item: IPageItem, copyChildren: boolean) => void;
  handleDuplicatePageItem: (item: IPageItem) => void;
  handlePastePage: (item: IPageItem) => void;
  expandedKeys: string[];
  draggingKey: string;
  image?: boolean;
}

export default observer(function CoursePageItem({
  nodeData,
  handleNewPageItem,
  handleCopyPageItem,
  handleDuplicatePageItem,
  handlePastePage,
  onExpand,
  draggingKey,
  expandedKeys,
  image = false,
}: ICoursePageItemProps) {
  const { data: page, key: pageID, children } = nodeData;
  const { courseStore } = useStore();
  const { courseViewModel, pageProperties, removePage, getPageProperties } = courseStore;
  const [onHover, setOnHover] = useState(false);
  const [imgLoading, setImgLoading] = useState(true);
  const theme = useTheme();
  const classes = coursePageStyles({ theme });

  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [removePageWarning, setRemovePageWarning] = useState('Are you sure you want to delete this page?');

  const [pageHasLastVisit, setPageHasLastVisit] = useState(false);
  const history = useHistory();
  const [Dialog, confirmDelete] = useConfirm('Delete page', removePageWarning);

  const imageBaseURL = process.env.REACT_APP_ENV_BASEURL;
  let url = '';
  if (page.avatar?.value) url = page.avatar.value.includes('https') ? page.avatar.value : 'https://' + imageBaseURL + '.norskinteraktiv.no/niasAPI' + page.avatar.value;

  const goToEdit = () => {
    history.push(`/courseEdit?courseID=${courseViewModel != undefined ? courseViewModel.courseID : 0}&pageID=${pageID}`);
  };

  const handleOpenMenu = (e: any) => {
    e.stopPropagation();
    setMenuAnchorEl(e.target);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuOpen(false);
  };

  const handleMenuClick = (aCopyChildren: boolean) => {
    handleCopyPageItem(nodeData, aCopyChildren);
    handleCloseMenu();
  };

  const handleMenuDuplicateClick = () => {
    handleDuplicatePageItem(nodeData);
    handleCloseMenu();
  };

  const handleRemovePage = async (e: any) => {
    setMenuOpen(false);
    e.stopPropagation();
    if (children.length > 0) {
      // eslint-disable-next-line quotes
      setRemovePageWarning("Are you sure you want to delete this page and it's subpages?");
    }

    const result = await confirmDelete();
    if (result) {
      removePage(String(pageID));
    }
  };

  const handleOpenPageProperties = () => {
    if (courseViewModel) {
      getPageProperties(courseViewModel.courseID, parseInt(pageID.toString()));
    }
  };

  useEffect(() => {
    const expanded = expandedKeys.includes(pageID as string);
    const lastPageID = courseViewModel?.lastPageVisited ? courseViewModel?.lastPageVisited : '0';
    const lastPageItem = findNodeById(children, lastPageID);
    if (lastPageItem && !expanded) {
      setPageHasLastVisit(true);
    } else {
      setPageHasLastVisit(false);
    }
  }, [courseViewModel, expandedKeys]);

  return (
    <>
      <div
        className='pageItem'
        data-drag='true' //used to find the target for the ghost element in CoursePageList.tsx
        style={{
          display: 'flex',
          flexDirection: 'column',
          pointerEvents: 'all',
        }}
      >
        <Box
          onClick={handleOpenPageProperties}
          onDoubleClick={goToEdit}
          display='flex'
          style={{
            justifyContent: 'space-between',
            height: image ? '80px' : '40px',
            backgroundColor: theme.palette.bgFour.main,
            borderRadius: 5,
            overflow: 'hidden',
            outline: '2px solid',
            color: theme.palette.bgFour.contrastText,
            outlineOffset: pageID === draggingKey ? 5 : 0,
            outlineColor: pageID === draggingKey ? theme.palette.secondary.main : pageID == pageProperties?.coursePageID ? theme.palette.primary.main : 'transparent',
            viewTransitionName: `page-bg-${pageID}`,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '50%',
            }}
          >
            <div
              style={{
                minWidth: image ? 110 : 0,
                maxWidth: image ? 110 : 0,
                height: image ? 61 : 0,
                background: theme.palette.bgThree.main,
                alignSelf: 'center',
                marginLeft: theme.spacing(1),
                marginRight: image ? theme.spacing(1) : 0,
                viewTransitionName: `page-image-${pageID}`,
              }}
            >
              <img
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                src={image ? url : 'https://niastest02.norskinteraktiv.no/niasAPI/img/thumb/DefaultCourseAvatar.jpg'}
                onLoad={() => setImgLoading(false)}
              />
            </div>
            <div style={{ viewTransitionName: `page-title-${pageID}`, width: '100%' }}>
              <Title className={classes.title} data={page} coursePageID={String(pageID)} fontSize={image ? 20 : undefined} onFocus={handleOpenPageProperties} />
            </div>
          </div>
          <Box display='flex' alignSelf='flex-end' justifyContent='flex-end' alignItems='center' style={{ minWidth: 190, height: '100%' }}>
            {pageHasLastVisit && (
              <Tooltip title='Last Visited Chapter' placement='top' tabIndex={1} arrow={true} classes={{ tooltip: classes.tooltip }} style={{ cursor: 'default' }}>
                <div onClick={(e) => onExpand(e, String(pageID))} className={classes.iconButton} style={{ display: 'flex', height: '20px', cursor: 'pointer' }}>
                  <PersonPinCircleDashed fontSize={'small'} />
                </div>
              </Tooltip>
            )}
            {courseViewModel?.lastPageVisited == String(pageID) && (
              <Tooltip title='Last Visited Page' placement='top' tabIndex={1} arrow={true} classes={{ tooltip: classes.tooltip }} style={{ cursor: 'default' }}>
                <PersonPinCircleIcon className={classes.iconButton} fontSize='small' style={{ viewTransitionName: `page-toolbar-pin-${pageID}` }} />
              </Tooltip>
            )}
            <Tooltip title='Page Properties' placement='top' tabIndex={1} arrow={true} classes={{ tooltip: classes.tooltip }}>
              <IconButton size='small' className={classes.iconButton} onClick={handleOpenPageProperties} style={{ viewTransitionName: `page-toolbar-settings-${pageID}` }}>
                <SettingsIcon fontSize='inherit' />
              </IconButton>
            </Tooltip>
            <Tooltip title='Edit Page' placement='top' tabIndex={1} arrow={true} classes={{ tooltip: classes.tooltip }}>
              <IconButton
                data-pagetour='3'
                size='small'
                className={classes.iconButton}
                component={Link}
                to={`/courseEdit?courseID=${courseViewModel != undefined ? courseViewModel.courseID : 0}&pageID=${pageID}`}
                style={{ viewTransitionName: `page-toolbar-edit-${pageID}` }}
              >
                <EditIcon fontSize='inherit' />
              </IconButton>
            </Tooltip>
            <Tooltip title='More' placement='top' tabIndex={1} arrow={true} classes={{ tooltip: classes.tooltip }}>
              <IconButton size='small' className={classes.iconButton} onClick={handleOpenMenu} style={{ viewTransitionName: `page-toolbar-more-${nodeData.key}` }}>
                <MoreHorizIcon />
              </IconButton>
            </Tooltip>
            <Menu
              id='basic-menu'
              anchorEl={menuAnchorEl}
              open={menuOpen}
              onClose={handleCloseMenu}
              MenuListProps={{
                'aria-labelledby': 'basic-button',
              }}
            >
              <MenuItem onClick={() => handleMenuDuplicateClick()}>
                <ListItemIcon>
                  <DuplicatePageIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText>Duplicate Page</ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => handleMenuClick(false)}>
                <ListItemIcon>
                  <CopyPageIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText>Copy Page</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => handleMenuClick(true)}>
                <ListItemIcon>
                  <CopyPageSubpageIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText>Copy Page and Subpages</ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem onClick={(e) => handleRemovePage(e)}>
                <ListItemIcon>
                  <DeleteIcon fontSize='medium' />
                </ListItemIcon>
                <ListItemText>Delete Page</ListItemText>
              </MenuItem>
            </Menu>
            <span
              style={{ minWidth: 10, maxWidth: 10, height: '100%', borderRadius: '0px 5px 5px 0px', background: getPageStatusColor(page), viewTransitionName: `page-toolbar-status-${pageID}` }}
            ></span>
          </Box>
        </Box>

        <div
          onMouseEnter={() => setOnHover(true)}
          onMouseLeave={() => setOnHover(false)}
          className={classes.plusBar}
          style={{ height: 6, display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', pointerEvents: draggingKey !== '' ? 'none' : 'auto' }}
        >
          {onHover && (
            <>
              <div className={classes.plusBarContainer} onClick={() => handleNewPageItem(nodeData)}>
                <div style={{ width: '100%', height: '100%' }}></div>
              </div>

              <ButtonGroup variant='contained' size='small' color='secondary' disableElevation aria-label='outlined primary button group' className={classes.plusBarButtonGroup}>
                <Tooltip title='Add New Page' placement='top' arrow={true} classes={{ tooltip: classes.tooltip }}>
                  <Button onClick={() => handleNewPageItem(nodeData)}>
                    <AddIcon fontSize='small' />
                  </Button>
                </Tooltip>
                <Tooltip title='Paste Page' placement='top' arrow={true} classes={{ tooltip: classes.tooltip }}>
                  <Button onClick={() => handlePastePage(nodeData)}>
                    <ContentPasteIcon fontSize='small' />
                  </Button>
                </Tooltip>
              </ButtonGroup>

              {/*             <div className={classes.plusBarStyleLeft}>
              <AddIcon fontSize='small' />
            </div>
            <div className={classes.plusBarStyleRight}>
              <ContentPasteIcon fontSize='small' style={{ fontSize: 15 }} />
            </div> */}
            </>
          )}
        </div>
      </div>

      <Dialog />
    </>
  );
});
